import { createContext, ReactNode, useContext, useEffect, useState } from 'react';

interface PendingBookingData {
  restaurant_id: number;
  waiter_id: number;
  starts_at: string;
  waiter_name: string;
  avatar_url?: string | null;
  reservation_id?: string | number | null;
}

interface RedirectContextType {
  redirectAfterLogin: string | null;
  setRedirectAfterLogin: (path: string | null) => void;
  clearRedirect: () => void;
  pendingBookingData: PendingBookingData | null;
  setPendingBookingData: (data: PendingBookingData | null) => void;
  clearPendingBookingData: () => void;
}

const RedirectContext = createContext<RedirectContextType | undefined>(undefined);

export function RedirectProvider({ children }: { children: ReactNode }) {
  const [redirectAfterLogin, setRedirectAfterLogin] = useState<string | null>(null);
  const [pendingBookingData, setPendingBookingData] = useState<PendingBookingData | null>(null);

  useEffect(() => {
    const savedRedirect = localStorage.getItem('redirectAfterLogin');
    const savedBookingData = localStorage.getItem('pendingBookingData');

    if (savedRedirect) {
      setRedirectAfterLogin(savedRedirect);
    }
    if (savedBookingData) {
      try {
        setPendingBookingData(JSON.parse(savedBookingData));
      } catch (error) {
        console.error('Error parsing pending booking data:', error);
        localStorage.removeItem('pendingBookingData');
      }
    }
  }, []);

  const clearRedirect = () => {
    setRedirectAfterLogin(null);
    localStorage.removeItem('redirectAfterLogin');
  };

  const clearPendingBookingData = () => {
    setPendingBookingData(null);
    localStorage.removeItem('pendingBookingData');
  };

  useEffect(() => {
    if (redirectAfterLogin) {
      localStorage.setItem('redirectAfterLogin', redirectAfterLogin);
    } else {
      localStorage.removeItem('redirectAfterLogin');
    }
  }, [redirectAfterLogin]);

  useEffect(() => {
    if (pendingBookingData) {
      localStorage.setItem('pendingBookingData', JSON.stringify(pendingBookingData));
    } else {
      localStorage.removeItem('pendingBookingData');
    }
  }, [pendingBookingData]);

  return (
    <RedirectContext.Provider
      value={{
        redirectAfterLogin,
        setRedirectAfterLogin,
        clearRedirect,
        pendingBookingData,
        setPendingBookingData,
        clearPendingBookingData
      }}
    >
      {children}
    </RedirectContext.Provider>
  );
}

export function useRedirect() {
  const context = useContext(RedirectContext);
  if (!context) {
    throw new Error('useRedirect must be used within RedirectProvider');
  }
  return context;
}
